import { Space } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  getWhatsappGroups,
  deleteWhatsappGroup,
} from "../../api/WhatsappGroupApi";
import { WhatsappGroupListForm } from "./WhatsappGroupListForm";

import { useNavigate } from "react-router-dom";
import AdnMessageError from "../common/adnMessage/AdnMessageError";
import AdnMessageSuccess from "../common/adnMessage/AdnMessageSuccess";
import adnYesNoModal from "../common/adnYesNoModal/AdnYesNoModal";
import { appTitle } from "../common/GeneralConfigurations";
import { AppRoutes } from "../../AppRoutes";

export const ManageWhatsappGroupListPage = () => {
  const { t } = useTranslation();
  const title = t("whatsappGroup.title");
  const [whatsappGroups, setWhatsappGroups] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getWhatsappGroupsData = useCallback(async () => {
    await getWhatsappGroups(searchName, orderBy).then((res) => {
      searchName.length >= 3
        ? setSearchOptions(res.data.result)
        : setSearchOptions([]);
      setWhatsappGroups(res.data.result);
    });
  }, [searchName, orderBy]);

  useEffect(() => {
    document.title = `${appTitle} - ${title}`;
  }, [title]);

  useEffect(() => {
    const fetchData = async () => {
      await getWhatsappGroupsData();
      setLoading(false);
    };
    fetchData();
  }, [searchName, orderBy, getWhatsappGroupsData]);

  const columns = [
    {
      title: t("whatsappGroup.columns.name"),
      dataIndex: "name",
      key: "name",
      sorter: true,
      width: "25%",
    },
    {
      title: t("whatsappGroup.columns.note"),
      dataIndex: "note",
      key: "note",
      width: "40%",
    },
    {
      title: t("whatsappGroup.columns.noteEN"),
      dataIndex: "noteEN",
      key: "noteEN",
      width: "40%",
    },
    {
      title: t("whatsappGroup.columns.link"),
      dataIndex: "link",
      key: "link",
      ellipsis: true,
      render: (text, record) => (
        <a target="_blank" href={record.link} rel="noreferrer">
          {record.link}
        </a>
      ),
      width: "30%",
    },
    {
      title: t("whatsappGroup.columns.action"),
      key: "action",
      render: (text, record) => (
        <Space
          size={"middle"}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <EditOutlined onClick={() => handleEdit(record.id)} />

          <DeleteOutlined
            style={{ color: "red" }}
            onClick={() => adnYesNoModal(async () => handleDelete(record.id))}
          />
        </Space>
      ),
      width: "10%",
    },
  ];

  const handleTableChanges = async (pagination, filters, sorter, extra) => {
    sorter.order === "descend"
      ? setOrderBy("-" + sorter.field)
      : setOrderBy(sorter.field);
  };

  const handleEdit = (consultantId) => {
    navigate(AppRoutes.whatsappGroupById.createPath(consultantId));
  };

  const handleAdd = () => {
    navigate(AppRoutes.whatsappGroup.path);
  };

  const handleDelete = async (id) => {
    const res = await deleteWhatsappGroup(id);
    if (res.status === 200) {
      getWhatsappGroupsData();
      AdnMessageSuccess();
    } else {
      AdnMessageError();
    }
  };

  const handleSearch = (value) => {
    let base64String = Buffer.from(value).toString("base64");
    value.length >= 3 ? setSearchName(base64String) : setSearchName("");
  };

  const handleSelect = (value) => {
    let base64String = Buffer.from(value).toString("base64");
    setSearchName(base64String);
  };

  return (
    <WhatsappGroupListForm
      title={title}
      handleSearch={handleSearch}
      handleSelect={handleSelect}
      handleAdd={handleAdd}
      handleTableChanges={handleTableChanges}
      whatsappGroups={whatsappGroups}
      searchOptions={searchOptions}
      columns={columns}
      loading={loading}
    />
  );
};
